// Общие части вкладок «Вес» и «Замеры»: форма записи, список записей с правкой и удалением.

import { useInfiniteQuery, useMutation } from '@tanstack/react-query'
import { useState, type FormEvent, type ReactNode } from 'react'

import { ApiError } from '@/api/client'
import { Button, EmptyState, ErrorText, Icon, Input, Modal, Spinner, useToast } from '@/components/ui'
import { formatDateTime, formatNumber, parseDecimal } from '@/lib/format'
import { metricKeys, metricsApi, type MetricSample, type MetricType } from '@/pages/metrics/api'
import { fromLocalInput, toLocalInput } from '@/pages/metrics/series'

import { fieldError, formError, SOURCE_LABELS, useInvalidateMetrics } from './helpers'

/** Добавление или правка одной записи. При ошибке введённое остаётся в полях. */
export function SampleDialog({
  open,
  onClose,
  type,
  sample,
  onDelete,
}: {
  open: boolean
  onClose: () => void
  type: MetricType
  sample?: MetricSample | null
  /** Правка существующей записи: удаление — внутри шторки (с «Отменить» в тосте). */
  onDelete?: () => void
}) {
  const toast = useToast()
  const invalidate = useInvalidateMetrics()
  const [value, setValue] = useState(sample ? String(sample.value).replace('.', ',') : '')
  const [at, setAt] = useState(toLocalInput(sample ? new Date(sample.measured_at) : new Date()))
  const [note, setNote] = useState(sample?.note ?? '')
  const [localError, setLocalError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: (parsed: number) => {
      const measured_at = fromLocalInput(at)
      return sample
        ? metricsApi.update(sample.id, { value: parsed, ...(measured_at ? { measured_at } : {}), note: note || null })
        : metricsApi.add({ metric: type.key, value: parsed, measured_at, note: note || null })
    },
    onSuccess: () => {
      void invalidate()
      toast.show({ message: sample ? 'Запись исправлена' : 'Записано', tone: 'accent' })
      onClose()
    },
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const parsed = parseDecimal(value)
    if (parsed === null) {
      setLocalError('Введи число')
      return
    }
    setLocalError(null)
    save.mutate(parsed)
  }

  const unit = type.unit || undefined
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={sample ? `Исправить: ${type.label_ru.toLowerCase()}` : `Записать: ${type.label_ru.toLowerCase()}`}
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Input
          label={type.label_ru}
          inputMode="decimal"
          autoFocus
          suffix={unit}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={localError ?? fieldError(save.error, 'value')}
        />
        <Input
          label="Дата и время"
          type="datetime-local"
          value={at}
          onChange={(e) => setAt(e.target.value)}
          error={fieldError(save.error, 'measured_at')}
        />
        <Input label="Заметка" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
        <ErrorText error={formError(save.error, ['value', 'measured_at'])} />
        <Button type="submit" variant="primary" loading={save.isPending}>
          Сохранить
        </Button>
        {sample && onDelete ? (
          <Button variant="ghost" className="self-center text-danger" onClick={onDelete}>
            Удалить запись
          </Button>
        ) : null}
      </form>
    </Modal>
  )
}

/** Записи метрики, новые сверху: тап по строке — правка (там же удаление с «Отменить», откат через журнал). */
export function SamplesList({ type, emptyText }: { type: MetricType; emptyText: string }) {
  const toast = useToast()
  const invalidate = useInvalidateMetrics()
  const [editing, setEditing] = useState<MetricSample | null>(null)
  const list = useInfiniteQuery({
    queryKey: metricKeys.samples(type.key),
    queryFn: ({ pageParam }) => metricsApi.samples(type.key, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
  })
  const undo = useMutation({
    mutationFn: metricsApi.undo,
    onSuccess: () => {
      void invalidate()
      toast.show({ message: 'Запись возвращена' })
    },
    onError: (error) => toast.show({ message: error instanceof ApiError ? error.message : 'Не получилось отменить', tone: 'danger' }),
  })
  const remove = useMutation({
    mutationFn: (sample: MetricSample) => metricsApi.remove(sample.id),
    onSuccess: ({ change_id }) => {
      void invalidate()
      toast.show({
        message: 'Запись удалена',
        action: { label: 'Отменить', onClick: () => undo.mutate(change_id) },
        duration: 8000,
      })
    },
    onError: (error) => toast.show({ message: error instanceof ApiError ? error.message : 'Не удалось удалить', tone: 'danger' }),
  })

  const items = list.data?.pages.flatMap((p) => p.items) ?? []
  if (list.isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    )
  }
  if (list.error) return <ErrorText error={list.error} />
  if (items.length === 0) return <p className="py-4 text-sm text-muted">{emptyText}</p>

  return (
    <>
      <ul className="divide-y divide-border">
        {items.map((s) => (
          <li key={s.id}>
            <button
              type="button"
              onClick={() => setEditing(s)}
              aria-label={`${formatNumber(s.value, { digits: 2 })} ${type.unit ?? ''}, ${formatDateTime(s.measured_at)}. Изменить`}
              className="-mx-2 flex min-h-12 w-[calc(100%+1rem)] items-center gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-surface-2 active:bg-surface-2"
            >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold tabular-nums text-fg">
                {formatNumber(s.value, { digits: 2 })}
                {type.unit ? <span className="font-normal text-muted"> {type.unit}</span> : null}
              </div>
              <div className="truncate text-xs text-muted">
                {formatDateTime(s.measured_at)} · {SOURCE_LABELS[s.source] ?? s.source}
                {s.note ? ` · ${s.note}` : ''}
              </div>
            </div>
            <Icon name="chevron-right" size={18} className="shrink-0 text-muted" />
            </button>
          </li>
        ))}
      </ul>
      {list.hasNextPage ? (
        <div className="mt-3">
          <Button size="sm" onClick={() => void list.fetchNextPage()} loading={list.isFetchingNextPage}>
            Показать ещё
          </Button>
        </div>
      ) : null}
      {editing ? (
        <SampleDialog
          key={editing.id}
          open
          onClose={() => setEditing(null)}
          type={type}
          sample={editing}
          onDelete={() => {
            remove.mutate(editing)
            setEditing(null)
          }}
        />
      ) : null}
    </>
  )
}

export function EmptyCard({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children?: ReactNode
}) {
  return (
    <EmptyState
      icon="health"
      title={title}
      description={description}
      action={children ? <div className="flex flex-wrap justify-center gap-2">{children}</div> : undefined}
    />
  )
}
